import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  withStyles,
  FormControlLabel,
  Dialog,
  DialogTitle as DialogTitleMUI,
  Typography,
  DialogActions as DialogActionsMUI,
  DialogContent as DialogContentMUI,
  DialogContentText as DialogContentTextMUI,
  Checkbox as CheckboxMUI,
} from '@material-ui/core';
import { Button as ButtonRSUI } from '@redshelf/redshelf-ui';
import { UserContentItemModifiersContext } from 'contexts';
import { IconButton } from 'components';
import Alert from '@material-ui/lab/Alert';
import { Close as CloseIcon } from '@material-ui/icons';

const useStyles = makeStyles((theme) => {
  return {
    paper: {
      [theme.breakpoints.up('md')]: {
        minWidth: theme.spacing(80),
      },
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(4, 1),
      },
    },
    closeButton: {
      color: '#666666',
    },
    dialogContentRoot: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: theme.spacing(3, 2.5),
    },
    alert: {
      background: `linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #ED6C02`,
      color: '#5F2B00',

      '& .MuiAlert-icon': {
        alignItems: 'center',
        color: '#ED6C02',
        marginRight: theme.spacing(1.25),

        '& svg': {
          width: theme.spacing(3),
          height: theme.spacing(3),
        },
      },
    },
    checkboxLabel: {
      margin: 0,
      color: theme.palette.greyBlue['700'],
    },
  };
});

const StyledDialogTitle = withStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2.5, 2, 2.5, 3),
    color: theme.palette.greyBlue[700],
  },
}))(DialogTitleMUI);

const Button = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 2),
    letterSpacing: '0.46px',
    lineHeight: '26px',
  },
}))(ButtonRSUI);

const DialogContentText = withStyles((theme) => ({
  root: {
    margin: 0,
    color: theme.palette.greyBlue[700],
  },
}))(DialogContentTextMUI);

const DialogActions = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
}))(DialogActionsMUI);

const Checkbox = withStyles((theme) => ({
  root: {
    color: theme.palette.greyBlue[600],
  },
}))(CheckboxMUI);

const DialogTitle = ({ onClose }) => {
  const classes = useStyles();

  return (
    <StyledDialogTitle disableTypography>
      <Typography variant="h5" id="offline-content-title-id">
        Access Your Content Offline
      </Typography>
      <IconButton
        aria-label="close"
        className={classes.closeButton}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
    </StyledDialogTitle>
  );
};

const DialogContent = ({ dontShowAgain, setDontShowAgain }) => {
  const classes = useStyles();

  return (
    <DialogContentMUI dividers classes={{ root: classes.dialogContentRoot }}>
      <Alert className={classes.alert} severity="warning">
        You’ve successfully downloaded content, but your content will not be
        available offline unless you bookmark or return to this page.
      </Alert>
      <DialogContentText>
        Add this webpage to your bookmarks or favorites within your browser
        while online and then return to this page when you are offline to access
        your downloaded content.
      </DialogContentText>
      <FormControlLabel
        control={
          <Checkbox
            checked={dontShowAgain}
            color="primary"
            onChange={() => setDontShowAgain((prev) => !prev)}
          />
        }
        label="Don't show this again"
        className={classes.checkboxLabel}
      />
    </DialogContentMUI>
  );
};

const AccessOfflineContentDialog = ({
  className,
  isOpen,
  closeAction,
  confirmAction,
  ...props
}) => {
  const classes = useStyles();

  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  const id = 'offlineContentDialog';
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    onDialogClose(id);
    closeAction();
  };

  useEffect(() => {
    if (isOpen) {
      setDontShowAgain(false);
    }
  }, [isOpen]);

  return (
    <Dialog
      className={className}
      classes={{ paper: classes.paper }}
      id={id}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="offline-content-title-id"
      TransitionProps={{ onEntered: () => onDialogOpen(id) }}
      {...props}
    >
      <DialogTitle onClose={handleClose} />
      <DialogContent
        dontShowAgain={dontShowAgain}
        setDontShowAgain={setDontShowAgain}
      />
      <DialogActions>
        <Button
          disableElevation
          onClick={() => {
            confirmAction(dontShowAgain);
            onDialogClose(id);
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AccessOfflineContentDialog.propTypes = {
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  closeAction: PropTypes.func,
  confirmAction: PropTypes.func,
};

export default AccessOfflineContentDialog;
